<template>
  <SolumLayout>
    <div class="dash">
      <!-- ====== Left column: building 3D · KPIs · filters ====== -->
      <aside class="dash-left">
        <!-- 건물 3D 뷰 — SSH(서울삼성병원) 건물 구조 미정으로 일시 블라인드. SVG/데이터 로직은 SOLUM 과 동일하게 유지. -->
        <section v-if="false" class="card bldg-card">
          <div class="card-eyebrow">BUILDING</div>
          <div class="bldg-title">서울삼성병원</div>
          <div class="bldg-sub">{{ buildingSub }}</div>

          <div class="bldg-stage">
            <svg
              :viewBox="`0 0 ${BV.W} ${BV.H}`"
              class="bldg-svg"
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label="건물 3D 뷰 - 층별 자산 현황"
            >
              <!-- 그림자 (지면) -->
              <ellipse
                :cx="BV.PAD_X + BV.FRONT_W / 2 + BV.DX / 2"
                :cy="BV.PAD_Y_TOP + BV.FRONT_H + 10"
                :rx="BV.FRONT_W / 2 + 10"
                ry="6"
                fill="rgba(15, 23, 42, 0.15)"
                filter="blur(2px)"
              />

              <!-- 윗면 (지붕) — 평행사변형 -->
              <polygon
                :points="topFacePoints"
                fill="url(#bldg-roof-grad)"
                stroke="#475569"
                stroke-width="1.2"
                stroke-linejoin="round"
              />

              <!-- 각 층 — 7개 슬롯, hasMap 인 것만 컬러+카운트, 나머지는 회색 -->
              <g v-for="(f, i) in buildingFloors" :key="f.floor_num">
                <!-- 측면 (오른쪽) -->
                <polygon
                  :points="sideFacePoints(i)"
                  :fill="f.hasMap ? floorColor(i).side : '#e2e8f0'"
                  :stroke="f.hasMap ? floorColor(i).stroke : '#cbd5e1'"
                  stroke-width="1"
                  stroke-linejoin="round"
                  :class="{
                    'bldg-face-current': f.hasMap && isFocusedMap(f.summary.map_id),
                    'bldg-face-disabled': !f.hasMap,
                  }"
                  class="bldg-face"
                  @click="onBldgFloorClick(f)"
                />
                <!-- 정면 -->
                <rect
                  :x="BV.PAD_X"
                  :y="BV.PAD_Y_TOP + i * floorH"
                  :width="BV.FRONT_W"
                  :height="floorH"
                  :fill="f.hasMap ? floorColor(i).fill : '#f1f5f9'"
                  :stroke="f.hasMap ? floorColor(i).stroke : '#cbd5e1'"
                  stroke-width="1"
                  :class="{
                    'bldg-face-current': f.hasMap && isFocusedMap(f.summary.map_id),
                    'bldg-face-disabled': !f.hasMap,
                  }"
                  class="bldg-face"
                  @click="onBldgFloorClick(f)"
                />

                <!-- 층 이름 (정면 좌측) — 빈 층은 옅은 회색 -->
                <text
                  :x="BV.PAD_X + 16"
                  :y="BV.PAD_Y_TOP + i * floorH + floorH / 2 + 2"
                  :fill="f.hasMap ? floorColor(i).text : '#94a3b8'"
                  font-size="15"
                  :font-weight="f.hasMap ? '800' : '600'"
                  dominant-baseline="middle"
                  pointer-events="none"
                >{{ f.name }}</text>

                <!-- 카운트 + 비율바 — hasMap 인 층만 -->
                <template v-if="f.hasMap">
                  <text
                    :x="BV.PAD_X + BV.FRONT_W - 14"
                    :y="BV.PAD_Y_TOP + i * floorH + floorH / 2 + 2"
                    :fill="floorColor(i).text"
                    font-size="13"
                    font-weight="600"
                    text-anchor="end"
                    dominant-baseline="middle"
                    pointer-events="none"
                  >
                    <tspan font-size="18" font-weight="800">{{ Number(f.summary.active_assets) || 0 }}</tspan><tspan
                      opacity="0.55"
                    > / {{ Number(f.summary.total_assets) || 0 }}</tspan>
                  </text>
                </template>
              </g>

              <!-- 외곽선 / 가장자리 (윗면→정면, 정면→측면 모서리 강조) -->
              <line
                :x1="BV.PAD_X" :y1="BV.PAD_Y_TOP"
                :x2="BV.PAD_X" :y2="BV.PAD_Y_TOP + BV.FRONT_H"
                stroke="#334155" stroke-width="1.2"
              />
              <line
                :x1="BV.PAD_X + BV.FRONT_W" :y1="BV.PAD_Y_TOP"
                :x2="BV.PAD_X + BV.FRONT_W" :y2="BV.PAD_Y_TOP + BV.FRONT_H"
                stroke="#334155" stroke-width="1.2"
              />
              <line
                :x1="BV.PAD_X + BV.FRONT_W + BV.DX" :y1="BV.PAD_Y_TOP - BV.DY"
                :x2="BV.PAD_X + BV.FRONT_W + BV.DX" :y2="BV.PAD_Y_TOP + BV.FRONT_H - BV.DY"
                stroke="#334155" stroke-width="1.2"
              />

              <defs>
                <linearGradient id="bldg-roof-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stop-color="#e2e8f0" />
                  <stop offset="100%" stop-color="#94a3b8" />
                </linearGradient>
              </defs>
            </svg>

            <!-- 키보드 네비게이션용 invisible 버튼 (접근성) -->
            <div class="bldg-kb-row">
              <button
                v-for="m in mapSummariesTopDown"
                :key="`kb-${m.map_id}`"
                class="bldg-kb-btn"
                :class="{ active: isFocusedMap(m.map_id) }"
                :title="`${m.floor_name}으로 포커스`"
                @click="focusFloor(m.map_id)"
              >{{ m.floor_name }}</button>
            </div>
          </div>
        </section>

        <section class="card kpi-card">
          <div class="kpi-label">TOTAL ASSETS</div>
          <div class="kpi-value">{{ totalAssets }}</div>
        </section>

        <section class="card kpi-card kpi-active">
          <div class="kpi-label">ACTIVE</div>
          <div class="kpi-value">{{ activeAssets }}</div>
          <div class="kpi-pct">{{ activePct }}%</div>
        </section>

        <section class="card kpi-card kpi-inactive">
          <div class="kpi-label">INACTIVE</div>
          <div class="kpi-value">{{ inactiveAssets }}</div>
          <div class="kpi-pct">{{ inactivePct }}%</div>
        </section>

        <section class="card">
          <div class="card-eyebrow">
            QUICK FILTERS
            <span v-if="activeFilterLabel" class="filter-active-hint">· {{ activeFilterLabel }}</span>
          </div>
          <div class="chips">
            <button
              v-for="f in filters"
              :key="f.id"
              class="chip"
              :class="{ active: activeFilter === f.id }"
              @click="activeFilter = f.id"
            >{{ f.label }}</button>
          </div>
          <p v-if="activeFilterLabel" class="filter-foot muted small">
            ALERTS · GATEWAYS 패널에 필터가 적용됩니다
          </p>
        </section>
      </aside>

      <!-- ====== Center: asset map + live feed ====== -->
      <section class="dash-center">
        <section class="card map-card">
          <header class="map-header">
            <div class="map-title-block">
              <h2 class="map-title">Asset map</h2>
              <p class="map-sub">{{ maps.length }} floors · {{ totalAssets }} tagged assets</p>
            </div>
            <div class="map-controls">
              <div class="mode-toggle" v-if="maps.length > 0">
                <button
                  class="mode-btn"
                  :class="{ active: mode === 'single' }"
                  @click="mode = 'single'"
                >▣ 단일</button>
                <button
                  class="mode-btn"
                  :class="{ active: mode === 'dual' }"
                  :disabled="maps.length < 2"
                  @click="mode = 'dual'"
                >▣▣ 듀얼</button>
              </div>
              <router-link to="/assetMap" class="map-link">관리 →</router-link>
            </div>
          </header>

          <div v-if="loadingMaps" class="map-state">맵 불러오는 중…</div>

          <div v-else-if="maps.length === 0" class="map-empty">
            <div class="map-empty-icon">🗺️</div>
            <p>업로드된 floorplan 이 없습니다</p>
            <router-link v-if="$isadmin === 1" to="/assetMap" class="map-empty-cta">맵 업로드하기 →</router-link>
          </div>

          <!-- Single mode -->
          <div v-else-if="mode === 'single'" class="map-single">
            <button
              class="arrow"
              :disabled="maps.length < 2"
              @click="prevMap"
              title="이전 층"
            >‹</button>
            <div class="map-slot">
              <div class="map-slot-header">
                <span class="floor-name">{{ currentMap.name }}</span>
                <span class="floor-pos">{{ currentIndex + 1 }} / {{ maps.length }}</span>
                <span v-if="currentSummary" class="floor-stats">
                  <strong class="active">{{ Number(currentSummary.active_assets) || 0 }}</strong>
                  <span class="muted small">/ {{ Number(currentSummary.total_assets) || 0 }} active</span>
                </span>
              </div>
              <AssetMapSolum :map="currentMap" :can-edit="false" />
            </div>
            <button
              class="arrow"
              :disabled="maps.length < 2"
              @click="nextMap"
              title="다음 층"
            >›</button>
          </div>

          <!-- Dual mode -->
          <div v-else-if="mode === 'dual'" class="map-dual">
            <div v-for="(slotIdx, i) in [0, 1]" :key="slotIdx" class="map-slot map-slot-half">
              <div class="map-slot-header">
                <select :value="dualMapIds[i]" class="floor-select" @change="setDualSlot(i, $event.target.value)">
                  <option v-for="m in maps" :key="m.id" :value="m.id">{{ m.name }}</option>
                </select>
                <span v-if="dualSummary(i)" class="floor-stats">
                  <strong class="active">{{ Number(dualSummary(i).active_assets) || 0 }}</strong>
                  <span class="muted small">/ {{ Number(dualSummary(i).total_assets) || 0 }} active</span>
                </span>
              </div>
              <AssetMapSolum v-if="dualMaps[i]" :map="dualMaps[i]" :can-edit="false" />
              <div v-else class="map-slot-empty">선택 안 됨</div>
            </div>
          </div>
        </section>

        <section class="card feed-card">
          <header class="feed-header">
            <h3 class="feed-title">LIVE MOVEMENT FEED</h3>
            <span class="feed-stream">
              <span class="dot-pulse"></span>
              <span>streaming</span>
            </span>
            <span class="muted small">updated {{ formatRelative(lastFeedAt) }}</span>
          </header>
          <div v-if="movements.length === 0" class="feed-empty">최근 이동 기록 없음</div>
          <ul v-else class="feed-list">
            <li
              v-for="(m, i) in movements"
              :key="m.idx"
              class="feed-item"
              :class="{ 'is-fresh': i === 0 }"
              role="button"
              tabindex="0"
              @click="goToNotificationCenter"
              @keydown.enter="goToNotificationCenter"
            >
              <div class="feed-top-row">
                <span class="feed-icon">📍</span>
                <span class="feed-rel">{{ formatRelative(m.datetime) }}</span>
              </div>
              <div class="feed-device">{{ m.devicename || (m.tagmac || '').slice(-6) }}</div>
              <div class="feed-path">
                <span class="loc-pill">{{ m.ori || '?' }}</span>
                <span class="feed-arrow">→</span>
                <span class="loc-pill loc-pill-dest">{{ m.destination || '?' }}</span>
              </div>
              <div class="feed-time-abs">{{ formatTime(m.datetime) }}</div>
            </li>
          </ul>
        </section>
      </section>

      <!-- ====== Right column: donut · alerts · gateways ====== -->
      <aside class="dash-right">
        <section class="card donut-card">
          <div class="card-eyebrow">ACTIVE VS INACTIVE</div>
          <div class="donut-wrap">
            <svg class="donut" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="50" stroke="#e5e7eb" stroke-width="14" fill="none" />
              <circle
                cx="60" cy="60" r="50"
                :stroke="activePct >= 50 ? '#10b981' : '#1e73ff'"
                stroke-width="14" fill="none"
                stroke-dasharray="314.159"
                :stroke-dashoffset="314.159 * (1 - activePct / 100)"
                stroke-linecap="round"
                transform="rotate(-90 60 60)"
              />
              <text x="60" y="56" text-anchor="middle" class="donut-pct">{{ activePct }}%</text>
              <text x="60" y="74" text-anchor="middle" class="donut-cap">ACTIVE</text>
            </svg>
          </div>
          <div class="donut-legend">
            <div><span class="dot dot-active"></span>Active <strong>{{ activeAssets }}</strong></div>
            <div><span class="dot dot-inactive"></span>Inactive <strong>{{ inactiveAssets }}</strong></div>
          </div>
        </section>

        <section class="card">
          <header class="row-header">
            <h3 class="row-title">ALERTS<span v-if="activeFilterLabel" class="filter-tag">· {{ activeFilterLabel }}</span></h3>
            <span class="badge badge-warn" v-if="filteredAlerts.length > 0">⚠ {{ filteredAlerts.length }}</span>
          </header>
          <div v-if="loadingAlerts" class="muted small">불러오는 중…</div>
          <ul v-else-if="filteredAlerts.length === 0" class="muted small">
            <li>{{ activeFilter === 'all' ? '활성 알림 없음' : '필터에 해당하는 알림 없음' }}</li>
          </ul>
          <ul v-else class="alert-list">
            <li
              v-for="a in filteredAlerts.slice(0, 5)"
              :key="a.id"
              class="alert-item"
              role="button"
              tabindex="0"
              @click="goToNotificationCenter"
              @keydown.enter="goToNotificationCenter"
            >
              <span class="alert-icon">{{ alertIcon(a.event_type) }}</span>
              <div class="alert-body">
                <div class="alert-line1">
                  <span class="alert-name">{{ a.devicename || (a.tag_mac || '').slice(-6) }}</span>
                  <span class="alert-time">{{ formatRelative(a.original_event_date || a.created_at) }}</span>
                </div>
                <div class="alert-line2 muted small">{{ alertMsg(a) }}</div>
              </div>
            </li>
          </ul>
        </section>

        <section class="card">
          <header class="row-header">
            <h3 class="row-title">GATEWAYS<span v-if="activeFilterLabel && activeFilter !== 'lowBattery'" class="filter-tag">· {{ activeFilterLabel }}</span></h3>
            <span class="badge" :class="gwOnlineCount === gwTotal ? 'badge-ok' : 'badge-warn'">
              {{ gwOnlineCount }}/{{ gwTotal }} online
            </span>
          </header>
          <ul v-if="filteredGateways.length === 0" class="muted small">
            <li>{{ activeFilter === 'all' ? '등록된 게이트웨이 없음' : '필터에 해당하는 게이트웨이 없음' }}</li>
          </ul>
          <ul v-else class="gw-list">
            <li v-for="gw in filteredGateways.slice(0, 6)" :key="gw.gw_mac" class="gw-item">
              <span class="dot" :class="Number(gw.active) === 0 ? 'dot-on' : 'dot-off'"></span>
              <span class="gw-name">{{ gw.devicename || gw.gw_mac.slice(-6) }}</span>
              <span class="gw-loc muted small">· {{ gw.devicelocation || '-' }}</span>
              <span class="gw-count">{{ gw.tag_count || 0 }}</span>
            </li>
          </ul>
        </section>
      </aside>
    </div>
  </SolumLayout>
</template>

<script>
import axios from 'axios';
import SolumLayout from './SolumLayout.vue';
import AssetMapSolum from './AssetMapSolum.vue';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'lowBattery', label: 'Low battery' },
  { id: 'offline', label: 'Offline' },
];

// SOLUM 사옥 — 건물 전체 층 수 (PoC 시연 기준).
// 실제 floorplan 이 업로드된 층(4·5층) 만 색상/카운트 표시,
// 나머지는 회색 슬랩으로 윤곽만.
const BUILDING_TOTAL_FLOORS = 7;

// 층별 컬러 팔레트 — 위층(인덱스 0) 부터 순환
const FLOOR_PALETTE = [
  { fill: '#dbeafe', side: '#bfdbfe', stroke: '#3b82f6', text: '#1e3a8a' }, // blue
  { fill: '#fee2e2', side: '#fecaca', stroke: '#ef4444', text: '#7f1d1d' }, // red
  { fill: '#dcfce7', side: '#bbf7d0', stroke: '#22c55e', text: '#14532d' }, // green
  { fill: '#fef3c7', side: '#fde68a', stroke: '#f59e0b', text: '#7c2d12' }, // amber
  { fill: '#ede9fe', side: '#ddd6fe', stroke: '#8b5cf6', text: '#4c1d95' }, // violet
  { fill: '#cffafe', side: '#a5f3fc', stroke: '#06b6d4', text: '#164e63' }, // cyan
];

export default {
  name: 'DashboardSSH',
  components: { SolumLayout, AssetMapSolum },
  data() {
    return {
      // KPIs
      totalAssets: 0,
      activeAssets: 0,
      inactiveAssets: 0,
      highTempAssets: 0,
      lowTempAssets: 0,
      lowBatteryAssets: 0,
      // maps
      maps: [],
      mapSummaries: [],
      loadingMaps: true,
      mode: 'dual',
      currentIndex: 0,
      dualMapIds: [null, null],
      // alerts / movements / gateways
      alerts: [],
      loadingAlerts: true,
      movements: [],
      lastFeedAt: null,
      gateways: [],
      // filters
      filters: FILTERS,
      activeFilter: 'all',
      // poll handles
      pollKpiId: null,
      pollFeedId: null,
      pollGwId: null,
    };
  },
  computed: {
    company() { return localStorage.getItem('company') || ''; },
    activePct() {
      if (!this.totalAssets) return 0;
      return Math.round((this.activeAssets / this.totalAssets) * 100);
    },
    inactivePct() {
      if (!this.totalAssets) return 0;
      return Math.round((this.inactiveAssets / this.totalAssets) * 100);
    },
    currentMap() { return this.maps[this.currentIndex] || null; },
    dualMaps() { return this.dualMapIds.map(id => this.maps.find(m => m.id === id) || null); },
    gwOnlineCount() { return this.gateways.filter(g => Number(g.active) === 0).length; },
    gwTotal() { return this.gateways.length; },
    /** Quick Filters 가 적용된 ALERTS / GATEWAYS 리스트 */
    filteredAlerts()   { return this.alerts.filter(a => this.matchAlertFilter(a)); },
    filteredGateways() { return this.gateways.filter(gw => this.matchGatewayFilter(gw)); },
    /** 현재 적용 중인 필터 라벨 (None 이면 null) */
    activeFilterLabel() {
      if (this.activeFilter === 'all') return null;
      const f = this.filters.find(x => x.id === this.activeFilter);
      return f ? f.label : null;
    },
    /** 위→아래(상층 우선) 정렬된 요약. 건물 3D 카드용.
        floor_name 에서 숫자(예: '5층' → 5)를 추출해 DESC. 동수면 floor_order DESC. */
    mapSummariesTopDown() {
      const num = (m) => {
        const match = String(m.floor_name || '').match(/(-?\d+)/);
        return match ? Number(match[1]) : 0;
      };
      return [...this.mapSummaries].sort((a, b) => {
        const d = num(b) - num(a);
        if (d !== 0) return d;
        return Number(b.floor_order) - Number(a.floor_order);
      });
    },
    /** 건물 SVG 뷰박스/좌표 — 항상 전체 층 수 기준으로 그림 */
    BV() {
      const n = BUILDING_TOTAL_FLOORS;
      const PAD_X = 20;
      const PAD_Y_TOP = 50;
      const FRONT_W = 200;
      const FRONT_H = Math.max(180, n * 36);
      const DX = 38;   // 깊이(가로) 오프셋
      const DY = 28;   // 깊이(세로) 오프셋 — 위로
      return {
        W: PAD_X + FRONT_W + DX + 24,
        H: PAD_Y_TOP + FRONT_H + 32,
        PAD_X, PAD_Y_TOP, FRONT_W, FRONT_H, DX, DY,
      };
    },
    /** 층 높이 (전체 층 N 등분, 항상 BUILDING_TOTAL_FLOORS 기준) */
    floorH() {
      return this.BV.FRONT_H / BUILDING_TOTAL_FLOORS;
    },
    /** 빌딩 위젯이 그릴 층 슬롯 — 위→아래(상층 우선).
        hasMap=true 인 층만 컬러/카운트 표시, 나머지는 회색 윤곽. */
    buildingFloors() {
      const out = [];
      for (let n = BUILDING_TOTAL_FLOORS; n >= 1; n--) {
        const summary = this.mapSummaries.find(s => {
          const m = String(s.floor_name || '').match(/(\d+)/);
          return m && Number(m[1]) === n;
        });
        out.push({
          floor_num: n,
          name: `${n}층`,
          hasMap: !!summary,
          summary: summary || null,
        });
      }
      return out;
    },
    /** 빌딩 부제목 — '7 floors · 34 assets' */
    buildingSub() {
      return `${BUILDING_TOTAL_FLOORS} floors · ${this.totalAssets} assets`;
    },
    /** 윗면(지붕) 평행사변형 4점 */
    topFacePoints() {
      const g = this.BV;
      const tl = `${g.PAD_X},${g.PAD_Y_TOP}`;
      const tr = `${g.PAD_X + g.FRONT_W},${g.PAD_Y_TOP}`;
      const br = `${g.PAD_X + g.FRONT_W + g.DX},${g.PAD_Y_TOP - g.DY}`;
      const bl = `${g.PAD_X + g.DX},${g.PAD_Y_TOP - g.DY}`;
      return `${tl} ${tr} ${br} ${bl}`;
    },
    /** 단일 모드 현재 맵의 summary */
    currentSummary() {
      if (!this.currentMap) return null;
      return this.mapSummaries.find(s => s.map_id === this.currentMap.id) || null;
    },
  },
  watch: {
    maps(newMaps) {
      if (newMaps.length < 2 && this.mode === 'dual') this.mode = 'single';
      if (this.currentIndex >= newMaps.length) this.currentIndex = Math.max(0, newMaps.length - 1);
      this.ensureDualSlots(newMaps);
    },
  },
  mounted() {
    this.fetchAll();
    this.pollKpiId = setInterval(() => this.fetchKpis(), 15000);
    this.pollFeedId = setInterval(() => { this.fetchAlerts(); this.fetchMovements(); }, 15000);
    this.pollGwId = setInterval(() => this.fetchGateways(), 10000);
    // 알림 acknowledge 같은 변화가 일어나면 즉시 ALERTS/KPI 새로고침
    this._notifChangedHandler = () => { this.fetchAlerts(); this.fetchKpis(); };
    window.addEventListener('notification:changed', this._notifChangedHandler);
  },
  beforeUnmount() {
    if (this.pollKpiId) clearInterval(this.pollKpiId);
    if (this.pollFeedId) clearInterval(this.pollFeedId);
    if (this.pollGwId) clearInterval(this.pollGwId);
    if (this._notifChangedHandler) {
      window.removeEventListener('notification:changed', this._notifChangedHandler);
    }
  },
  methods: {
    async fetchAll() {
      await Promise.all([
        this.fetchKpis(),
        this.fetchMaps(),
        this.fetchSummaries(),
        this.fetchAlerts(),
        this.fetchMovements(),
        this.fetchGateways(),
      ]);
    },
    async fetchKpis() {
      try {
        const r = await axios.get('/api/dashboard', { params: { company: this.company } });
        const d = r.data || {};
        this.totalAssets = d.totalAssets || 0;
        this.activeAssets = d.activeAssets || 0;
        this.inactiveAssets = d.inactiveAssets || 0;
        this.highTempAssets = d.highTempAssets || 0;
        this.lowTempAssets = d.lowTempAssets || 0;
        this.lowBatteryAssets = d.lowBatteryAssets || 0;
      } catch (e) {
        console.error('dashboard KPIs failed', e);
      }
    },
    async fetchMaps() {
      this.loadingMaps = true;
      try {
        const r = await axios.get('/api/asset-maps', { params: { company: this.company } });
        this.maps = r.data?.data || [];
        this.ensureDualSlots(this.maps);
      } catch (e) {
        console.error('maps fetch failed', e);
        this.maps = [];
      } finally {
        this.loadingMaps = false;
      }
    },
    async fetchSummaries() {
      try {
        const r = await axios.get('/api/asset-maps/summary', { params: { company: this.company } });
        this.mapSummaries = r.data?.data || [];
      } catch (e) {
        console.error('summaries failed', e);
        this.mapSummaries = [];
      }
    },
    async fetchAlerts() {
      this.loadingAlerts = true;
      try {
        const r = await axios.get('/api/asset-maps/recent-alerts', {
          params: { company: this.company, limit: 10 },
        });
        this.alerts = r.data?.data || [];
      } catch (e) {
        console.error('alerts failed', e);
        this.alerts = [];
      } finally {
        this.loadingAlerts = false;
      }
    },
    async fetchMovements() {
      try {
        const r = await axios.get('/api/asset-maps/recent-movements', {
          params: { company: this.company, limit: 5 },
        });
        this.movements = r.data?.data || [];
        this.lastFeedAt = new Date();
      } catch (e) {
        console.error('movements failed', e);
        this.movements = [];
      }
    },
    async fetchGateways() {
      try {
        const r = await axios.get('/api/gatewayList', {
          params: { company: this.company, limit: 50 },
        });
        this.gateways = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      } catch (e) {
        this.gateways = [];
      }
    },
    ensureDualSlots(maps) {
      const valid = (id) => maps.some(m => m.id === id);
      const pick = [];
      pick[0] = valid(this.dualMapIds[0]) ? this.dualMapIds[0] : (maps[0]?.id || null);
      pick[1] = valid(this.dualMapIds[1]) ? this.dualMapIds[1] : (maps[1]?.id || maps[0]?.id || null);
      this.dualMapIds = pick;
    },
    setDualSlot(i, id) {
      const next = [...this.dualMapIds]; next[i] = id; this.dualMapIds = next;
    },
    prevMap() {
      if (this.maps.length < 2) return;
      this.currentIndex = (this.currentIndex - 1 + this.maps.length) % this.maps.length;
    },
    nextMap() {
      if (this.maps.length < 2) return;
      this.currentIndex = (this.currentIndex + 1) % this.maps.length;
    },
    barPct(m) {
      if (!m.total_assets || m.total_assets == 0) return 0;
      return Math.round((Number(m.active_assets) / Number(m.total_assets)) * 100);
    },
    /** 듀얼 모드 슬롯의 summary */
    dualSummary(i) {
      const id = this.dualMapIds[i];
      if (!id) return null;
      return this.mapSummaries.find(s => s.map_id === id) || null;
    },
    /** Quick Filters 가 ALERTS 패널에 적용되었을 때 매칭되는 항목만 반환 */
    matchAlertFilter(a) {
      const f = this.activeFilter;
      const t = String(a.event_type || '').toLowerCase();
      if (f === 'all' || f === 'active') return true;
      if (f === 'lowBattery') return /battery/.test(t);
      if (f === 'offline')    return /offline|disconnect|inactive/.test(t);
      if (f === 'inactive')   return /offline|disconnect|missing|inactive/.test(t);
      return true;
    },
    /** Quick Filters 가 GATEWAYS 패널에 적용되었을 때 매칭되는 항목만 반환 */
    matchGatewayFilter(gw) {
      const f = this.activeFilter;
      if (f === 'all' || f === 'lowBattery') return true; // GW 자체엔 배터리 개념 없음
      const connected = Number(gw.active) === 0;
      if (f === 'active')                       return connected;
      if (f === 'inactive' || f === 'offline')  return !connected;
      return true;
    },
    /** 건물 3D 뷰에서 강조할 맵인지 — 단일 모드는 currentMap, 듀얼은 양쪽 슬롯 */
    isFocusedMap(mapId) {
      if (this.mode === 'single') return this.currentMap?.id === mapId;
      return this.dualMapIds.includes(mapId);
    },
    /** 건물 카드의 한 층을 클릭 → 단일 모드로 전환 + 해당 층으로 포커스 */
    focusFloor(mapId) {
      const idx = this.maps.findIndex(m => m.id === mapId);
      if (idx < 0) return;
      this.mode = 'single';
      this.currentIndex = idx;
    },
    /** 빌딩 위젯에서 층 클릭. hasMap 인 층만 동작. */
    onBldgFloorClick(f) {
      if (f && f.hasMap && f.summary) {
        this.focusFloor(f.summary.map_id);
      }
    },
    /** 층 색상 팔레트 (인덱스 순환) */
    floorColor(i) {
      return FLOOR_PALETTE[i % FLOOR_PALETTE.length];
    },
    /** 측면(오른쪽) 평행사변형 4점 — 인덱스 i 의 층 */
    sideFacePoints(i) {
      const g = this.BV;
      const tl_x = g.PAD_X + g.FRONT_W;
      const tl_y = g.PAD_Y_TOP + i * this.floorH;
      const tr_x = tl_x + g.DX;
      const tr_y = tl_y - g.DY;
      const br_x = tr_x;
      const br_y = tr_y + this.floorH;
      const bl_x = tl_x;
      const bl_y = tl_y + this.floorH;
      return `${tl_x},${tl_y} ${tr_x},${tr_y} ${br_x},${br_y} ${bl_x},${bl_y}`;
    },
    alertIcon(type) {
      if (!type) return '⚠️';
      const t = String(type).toLowerCase();
      if (t.includes('battery')) return '🔋';
      if (t.includes('temp')) return '🌡';
      if (t.includes('offline') || t.includes('disconnect')) return '📡';
      if (t.includes('move') || t.includes('lost')) return '📍';
      return '⚠️';
    },
    alertMsg(a) {
      if (a.event_msg) return a.event_msg;
      const t = (a.event_type || '').toLowerCase();
      if (t.includes('battery')) return `Battery ${a.event_value != null ? Number(a.event_value).toFixed(1) + 'V' : ''}`;
      if (t.includes('temp')) return `Temp ${a.event_value != null ? Number(a.event_value).toFixed(1) + '°C' : ''}`;
      if (t.includes('offline') || t.includes('disconnect')) return 'Offline';
      return a.event_type || 'Alert';
    },
    formatTime(t) {
      if (!t) return '';
      const d = new Date(t);
      if (isNaN(d.getTime())) return '';
      return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    },
    formatRelative(t) {
      if (!t) return '-';
      const d = new Date(t);
      if (isNaN(d.getTime())) return '-';
      const sec = Math.floor((Date.now() - d.getTime()) / 1000);
      if (sec < 60) return 'just now';
      if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
      if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
      return `${Math.floor(sec / 86400)}d ago`;
    },
    goToNotificationCenter() {
      this.$router.push('/notificationCenter');
    },
  },
};
</script>

<style scoped>
/* ====== Layout: 3-column grid ====== */
.dash {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr) 320px;
  gap: 16px;
  align-items: start;
}
@media (max-width: 1280px) {
  .dash { grid-template-columns: 240px minmax(0, 1fr) 280px; gap: 12px; }
}
@media (max-width: 1024px) {
  .dash { grid-template-columns: 1fr; }
}

.dash-left, .dash-center, .dash-right {
  display: flex; flex-direction: column; gap: 12px;
  min-width: 0;
}

/* ====== Card base ====== */
.card {
  background: #ffffff; border: 1px solid #e5e7eb; border-radius: 10px;
  padding: 14px 16px;
}
.eyebrow, .card-eyebrow {
  font-size: 10px; font-weight: 600; letter-spacing: 0.6px;
  color: #9ca3af; text-transform: uppercase;
  margin-bottom: 6px;
}

.muted { color: #9ca3af; }
.small { font-size: 11px; }

/* ====== Snapshot (legacy — 미사용) ====== */
.snapshot { padding: 16px; }
.snap-title { font-size: 18px; font-weight: 700; color: #0a1f44; margin-top: 4px; }
.snap-sub { font-size: 12px; color: #6b7280; margin-top: 2px; }

/* ====== Building 3D (SVG isometric) ====== */
.bldg-card { padding: 14px 16px 16px; }
.bldg-title { font-size: 17px; font-weight: 700; color: #0a1f44; margin-top: 4px; }
.bldg-sub { font-size: 11px; color: #6b7280; margin: 2px 0 12px; }
.bldg-empty { padding: 12px 0; text-align: center; }

.bldg-stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 4px 0 0;
}
.bldg-svg {
  width: 100%;
  max-width: 280px;
  height: auto;
  display: block;
}
.bldg-svg .bldg-face {
  cursor: pointer;
  transition: filter 0.2s ease, opacity 0.2s ease;
}
.bldg-svg .bldg-face:hover {
  filter: brightness(1.06) saturate(1.1);
}
.bldg-svg .bldg-face-current {
  filter: brightness(1.04) saturate(1.2);
}
.bldg-svg .bldg-face-current[stroke] {
  stroke-width: 2;
}
/* 데이터 없는(맵 미업로드) 층 — 회색 슬랩, 클릭 비활성 */
.bldg-svg .bldg-face-disabled {
  cursor: default;
}
.bldg-svg .bldg-face-disabled:hover {
  filter: none;
}

/* 키보드/터치 네비게이션 보조 버튼 */
.bldg-kb-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  width: 100%;
}
.bldg-kb-btn {
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  padding: 4px 10px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}
.bldg-kb-btn:hover { background: #f3f4f6; border-color: #94a3b8; }
.bldg-kb-btn.active {
  background: #1e73ff; color: #ffffff; border-color: #1e73ff;
}

/* ====== KPI cards ====== */
.kpi-card { padding: 14px 16px; }
.kpi-label { font-size: 10px; font-weight: 600; letter-spacing: 0.6px; color: #6b7280; text-transform: uppercase; }
.kpi-value { font-size: 28px; font-weight: 700; color: #111827; line-height: 1.1; margin-top: 4px; }
.kpi-pct { font-size: 11px; color: #9ca3af; margin-top: 2px; }
.kpi-active .kpi-value { color: #10b981; }
.kpi-inactive .kpi-value { color: #1e73ff; }

/* ====== Floor list (active ratio per floor) ====== */
.floor-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
.floor-row { display: flex; flex-direction: column; gap: 4px; }
.floor-row-top { display: flex; justify-content: space-between; align-items: center; font-size: 12px; }
.floor-row-top .floor-name { font-weight: 600; color: #111827; }
.floor-frac { color: #374151; }
.floor-bar { height: 6px; background: #f1f5f9; border-radius: 99px; overflow: hidden; }
.floor-bar-fill {
  height: 100%; border-radius: 99px;
  background: linear-gradient(90deg, #10b981, #34d399);
  transition: width 0.5s ease;
}

/* ====== Quick filters ====== */
.chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chip {
  background: #f3f4f6; border: 1px solid transparent; color: #374151;
  padding: 5px 10px; border-radius: 99px; font-size: 11px; cursor: pointer;
  transition: all 0.15s ease;
}
.chip:hover { background: #e5e7eb; }
.chip.active { background: #0a1f44; color: white; }
.filter-active-hint {
  margin-left: 4px; color: #1e73ff; font-weight: 700; text-transform: none;
  letter-spacing: 0;
}
.filter-foot { margin: 8px 0 0 0; }
/* ALERTS / GATEWAYS 헤더에 표시되는 필터 태그 */
.filter-tag {
  margin-left: 6px; font-size: 10px; font-weight: 600; color: #1e73ff;
  background: #eff6ff; padding: 1px 6px; border-radius: 99px;
  text-transform: none; letter-spacing: 0;
}

/* ====== Center: map card ====== */
.map-card { padding: 14px 16px 16px; }
.map-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 12px; flex-wrap: wrap; margin-bottom: 12px;
}
.map-title { margin: 0; font-size: 16px; font-weight: 700; color: #111827; }
.map-sub { margin: 2px 0 0 0; font-size: 12px; color: #6b7280; }
.map-controls { display: flex; gap: 8px; align-items: center; }

.mode-toggle { display: flex; border: 1px solid #d1d5db; border-radius: 6px; overflow: hidden; }
.mode-btn {
  background: #ffffff; border: none; padding: 5px 10px; font-size: 11px; color: #374151;
  cursor: pointer; border-right: 1px solid #d1d5db;
}
.mode-btn:last-child { border-right: none; }
.mode-btn:hover:not(:disabled) { background: #f3f4f6; }
.mode-btn.active { background: #1e73ff; color: white; }
.mode-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.map-link { font-size: 12px; color: #1e73ff; text-decoration: none; font-weight: 500; }
.map-link:hover { text-decoration: underline; }

.map-state, .map-empty {
  padding: 40px 24px; text-align: center; color: #6b7280; font-size: 13px;
  border: 1px dashed #cbd5e1; border-radius: 8px; background: #f9fafb;
}
.map-empty-icon { font-size: 36px; }
.map-empty p { margin: 8px 0 4px 0; }
.map-empty-cta { font-size: 12px; color: #1e73ff; text-decoration: none; }

.map-single { display: flex; gap: 8px; align-items: stretch; }
.map-dual { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 1100px) { .map-dual { grid-template-columns: 1fr; } }

.map-slot {
  flex: 1; min-width: 0;
  border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; background: #ffffff;
}
.map-slot-header {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 8px; margin-bottom: 8px; border-bottom: 1px solid #f1f5f9;
}
.map-slot-header .floor-name { font-size: 14px; font-weight: 700; color: #111827; }
.map-slot-header .floor-pos {
  font-size: 11px; color: #6b7280; background: #f3f4f6;
  padding: 2px 8px; border-radius: 10px;
}
.map-slot-header .floor-stats {
  margin-left: auto;
  display: flex; align-items: baseline; gap: 4px;
  background: #ecfdf5; padding: 3px 10px; border-radius: 99px;
  font-size: 12px;
}
.map-slot-header .floor-stats .active { color: #047857; font-size: 14px; font-weight: 700; }
.floor-select {
  border: 1px solid #d1d5db; border-radius: 6px; padding: 4px 8px;
  font-size: 13px; font-weight: 600; color: #111827; background: #ffffff;
}
.map-slot-empty {
  padding: 60px 16px; text-align: center; color: #9ca3af; font-size: 12px;
  background: #f9fafb; border-radius: 6px;
}
.arrow {
  flex: 0 0 36px; width: 36px;
  background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px;
  font-size: 22px; color: #6b7280; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s ease;
}
.arrow:hover:not(:disabled) { background: #1e73ff; color: white; border-color: #1e73ff; }
.arrow:disabled { opacity: 0.3; cursor: not-allowed; }

/* ====== Live movement feed ====== */
.feed-card { padding: 14px 16px; }
.feed-header { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
.feed-title { margin: 0; font-size: 11px; font-weight: 600; letter-spacing: 0.6px; color: #6b7280; text-transform: uppercase; }
.feed-stream { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #10b981; font-weight: 500; }
.dot-pulse {
  width: 6px; height: 6px; border-radius: 50%; background: #10b981;
  animation: pulse 1.5s infinite;
}
@keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; } }
.feed-empty {
  padding: 24px; text-align: center; color: #9ca3af; font-size: 12px;
  background: #f9fafb; border-radius: 8px;
}
.feed-list {
  list-style: none; padding: 0; margin: 0;
  display: grid; grid-auto-flow: column; grid-auto-columns: minmax(170px, 1fr);
  gap: 10px; overflow-x: auto;
  scrollbar-width: thin;
}
.feed-item {
  position: relative;
  padding: 12px 14px;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
  border: 1px solid #eef0f3;
  border-radius: 10px;
  display: flex; flex-direction: column; gap: 6px;
  min-width: 170px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  outline: none;
}
.feed-item:hover, .feed-item:focus-visible {
  transform: translateY(-2px);
  border-color: #c7d2fe;
  box-shadow: 0 6px 16px rgba(30, 64, 175, 0.08);
}
.feed-item:focus-visible { box-shadow: 0 0 0 2px rgba(30, 115, 255, 0.4), 0 6px 16px rgba(30, 64, 175, 0.08); }
.feed-item.is-fresh {
  background: linear-gradient(180deg, #ecfdf5 0%, #ffffff 70%);
  border-color: #6ee7b7;
}
.feed-item.is-fresh::before {
  content: '';
  position: absolute; top: 10px; right: 10px;
  width: 6px; height: 6px; border-radius: 50%;
  background: #10b981; box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.18);
  animation: pulse 1.5s infinite;
}

.feed-top-row { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.feed-icon { font-size: 14px; }
.feed-rel {
  font-size: 11px; font-weight: 600; color: #1e73ff;
  background: #eff6ff; padding: 2px 8px; border-radius: 99px;
}
.feed-item.is-fresh .feed-rel { color: #047857; background: #d1fae5; }

.feed-device {
  font-size: 13px; color: #111827; font-weight: 700;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.feed-path {
  display: flex; align-items: center; gap: 4px;
  font-size: 11px; color: #6b7280; flex-wrap: wrap;
}
.loc-pill {
  background: #f3f4f6; color: #374151; font-weight: 500;
  padding: 2px 7px; border-radius: 6px; font-size: 11px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
}
.loc-pill-dest { background: #dbeafe; color: #1e40af; font-weight: 600; }
.feed-arrow { color: #9ca3af; font-size: 12px; padding: 0 2px; }
.feed-time-abs { font-size: 10px; color: #9ca3af; font-family: ui-monospace, monospace; }

/* ====== Right column: donut ====== */
.donut-card { padding: 14px 16px 12px; text-align: center; }
.donut-wrap { display: flex; justify-content: center; padding: 4px 0; }
.donut { width: 160px; height: 160px; }
.donut-pct { font-size: 22px; font-weight: 800; fill: #111827; }
.donut-cap { font-size: 9px; font-weight: 600; fill: #6b7280; letter-spacing: 0.5px; }
.donut-legend { display: flex; justify-content: space-around; margin-top: 8px; font-size: 12px; color: #374151; }
.donut-legend strong { font-weight: 700; color: #111827; margin-left: 4px; }
.dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 4px; }
.dot-active, .dot-on { background: #10b981; }
.dot-inactive, .dot-off { background: #ef4444; }

/* ====== Alerts / Gateways shared ====== */
.row-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.row-title { margin: 0; font-size: 11px; font-weight: 600; letter-spacing: 0.6px; color: #6b7280; text-transform: uppercase; }
.badge { font-size: 11px; padding: 2px 8px; border-radius: 99px; font-weight: 600; }
.badge-warn { background: #fef3c7; color: #b45309; }
.badge-ok { background: #d1fae5; color: #047857; }

.alert-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.alert-item {
  display: flex; gap: 8px; padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.1s ease, transform 0.1s ease;
  outline: none;
}
.alert-item:hover, .alert-item:focus-visible {
  background: #f9fafb;
  transform: translateX(2px);
}
.alert-item:focus-visible { box-shadow: 0 0 0 2px rgba(30, 115, 255, 0.4); }
.alert-icon { font-size: 18px; flex: 0 0 24px; text-align: center; padding-top: 2px; }
.alert-body { flex: 1; min-width: 0; }
.alert-line1 { display: flex; justify-content: space-between; gap: 6px; align-items: baseline; }
.alert-name { font-size: 13px; font-weight: 600; color: #111827; }
.alert-time { font-size: 10px; color: #9ca3af; }
.alert-line2 { margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.gw-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
.gw-item { display: flex; align-items: center; gap: 6px; padding: 4px 0; font-size: 12px; }
.gw-name { font-weight: 600; color: #111827; }
.gw-loc { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; }
.gw-count {
  font-size: 11px; color: #6b7280; background: #f3f4f6;
  padding: 1px 6px; border-radius: 8px; font-weight: 600;
}
</style>
